import { api } from './client';
import type { Actor } from '../types/entities/actor.entity';

export interface MusicTrack {
    id: string;
    title: string;
    artist: string | null;
    album: string | null;
    album_path?: string | null;
    track_number?: number | null;
    source?: string | null;
    license?: string | null;
    source_url?: string | null;
    /** 0 = not rated, 1..5 stars. */
    rating?: number;
    checksum_sha256?: string | null;
    duration_seconds: number | null;
    mime_type: string | null;
    size_bytes: number | null;
    original_name: string | null;
    stream_url: string;
    stream_url_expires_at: string;
    position?: number;
    created_by?: Actor | null;
    created_at: string;
    updated_at: string;
}

export interface MusicAlbumRow {
    album_path: string | null;
    album: string | null;
    artist: string | null;
    track_count: number;
    total_size_bytes: number;
    updated_at?: string | null;
    /** Direct stats (tracks stored exactly at album_path). */
    total_duration_seconds?: number;
    rated_count?: number;
    /** Mean over rated tracks (rating > 0), 1 decimal, null when none rated. */
    avg_rating?: number | null;
    /** Recursive stats (album + all sub-albums). */
    parent_path?: string | null;
    depth?: number;
    track_count_recursive?: number;
    total_size_bytes_recursive?: number;
    total_duration_seconds_recursive?: number;
    rated_count_recursive?: number;
    avg_rating_recursive?: number | null;
}

export interface MusicManifestTrack {
    id: string;
    title: string;
    filename: string;
    album_path: string | null;
    size_bytes: number | null;
    download_url: string;
}

export interface MusicManifest {
    track_count: number;
    total_size_bytes: number;
    tracks: MusicManifestTrack[];
}

export type MusicTrackUpdate = Partial<Pick<MusicTrack,
    'title' | 'artist' | 'album' | 'album_path' | 'track_number' | 'source' | 'license' | 'source_url' | 'duration_seconds' | 'rating'>>;

export type MusicSearchSort = 'title' | 'artist' | 'album' | 'rating' | 'duration' | 'size' | 'created_at';
export type MusicSearchField = 'title' | 'artist' | 'album' | 'filename';

/** Server-side advanced search (GET /music/tracks with filter params). */
export interface MusicSearchParams {
    q?: string;
    exact?: boolean;
    fields?: MusicSearchField[];
    albumPath?: string;
    source?: string;
    sourceNot?: string;
    rating?: number;
    minRating?: number;
    extensions?: string[];
    minDuration?: number;
    maxDuration?: number;
    sort?: MusicSearchSort;
    direction?: 'asc' | 'desc';
    page?: number;
    perPage?: number;
}

export interface MusicSearchResult {
    tracks: MusicTrack[];
    total: number;
    page: number;
    lastPage: number;
}

export interface MusicPlaylist {
    id: string;
    name: string;
    description: string | null;
    tracks_count: number;
    track_ids: string[] | null;
    tracks: MusicTrack[] | null;
    created_at: string;
    updated_at: string;
}

export type MusicRepeatMode = 'off' | 'one' | 'all';

export type MusicCommandAction =
    | 'play' | 'pause' | 'toggle' | 'stop' | 'next' | 'prev' | 'seek' | 'volume' | 'load' | 'shuffle' | 'repeat';

export interface MusicPlayerState {
    player: string;
    revision: number;
    track_id: string | null;
    track: MusicTrack | null;
    playlist_id: string | null;
    queue: string[];
    position_seconds: number;
    is_playing: boolean;
    volume: number;
    shuffle: boolean;
    repeat: MusicRepeatMode;
    last_command: {
        revision: number;
        action: MusicCommandAction;
        value: unknown;
        track_id: string | null;
        by: string | null;
        at: string;
    } | null;
    updated_by: Actor | null;
    updated_at: string;
}

/** Name of the browser player in the remote-control API. */
export const WEB_PLAYER_NAME = 'web';

export const musicService = {
    /** Loads every page (500 per page), optionally limited to an album node and all its sub-albums. */
    tracks: async (search = '', albumPath = ''): Promise<MusicTrack[]> => {
        const all: MusicTrack[] = [];
        let page = 1;
        let lastPage = 1;
        do {
            const query = new URLSearchParams({ per_page: '500', page: String(page) });
            if (search.trim()) {
                query.set('search', search.trim());
            }
            if (albumPath) {
                query.set('album_path', albumPath);
            }
            const response = await api.get<{ data: { tracks: MusicTrack[]; meta?: { lastPage?: number } } }>(`/music/tracks?${query.toString()}`);
            all.push(...(response.data.data.tracks ?? []));
            lastPage = response.data.data.meta?.lastPage ?? 1;
            page += 1;
        } while (page <= lastPage);
        return all;
    },
    search: async (params: MusicSearchParams): Promise<MusicSearchResult> => {
        const query = new URLSearchParams({ per_page: String(params.perPage ?? 50), page: String(params.page ?? 1) });
        const set = (key: string, value: string | number | undefined) => {
            if (value !== undefined && value !== '') query.set(key, String(value));
        };
        set('q', params.q?.trim());
        if (params.q?.trim() && params.exact) query.set('exact', '1');
        params.fields?.forEach((field) => query.append('fields[]', field));
        set('album_path', params.albumPath);
        set('source', params.source);
        set('source_not', params.sourceNot);
        set('rating', params.rating);
        set('min_rating', params.minRating);
        params.extensions?.forEach((extension) => query.append('extension[]', extension));
        set('min_duration', params.minDuration);
        set('max_duration', params.maxDuration);
        set('sort', params.sort);
        set('direction', params.direction);
        const response = await api.get<{ data: { tracks: MusicTrack[]; meta?: { total?: number; currentPage?: number; lastPage?: number } } }>(`/music/tracks?${query.toString()}`);
        const { tracks, meta } = response.data.data;
        return { tracks: tracks ?? [], total: meta?.total ?? tracks?.length ?? 0, page: meta?.currentPage ?? 1, lastPage: meta?.lastPage ?? 1 };
    },
    /** `includeParents` adds empty ancestor folders (track_count 0) so every level of the hierarchy has a row with recursive stats. */
    albums: async (includeParents = false): Promise<MusicAlbumRow[]> => {
        const response = await api.get<{ data: { albums: MusicAlbumRow[] } }>('/music/albums', { params: includeParents ? { include_parents: 1 } : undefined });
        return response.data.data.albums ?? [];
    },
    downloadUrl: async (id: string): Promise<{ url: string; filename: string; expires_at: string }> => {
        const response = await api.get<{ data: { url: string; filename: string; expires_at: string } }>(`/music/tracks/${encodeURIComponent(id)}/download-url`);
        return response.data.data;
    },
    manifest: async (albumPath = '', minRating = 0): Promise<MusicManifest> => {
        const query = new URLSearchParams();
        if (albumPath) {
            query.set('album_path', albumPath);
        }
        if (minRating > 0) {
            query.set('min_rating', String(minRating));
        }
        const response = await api.get<{ data: MusicManifest }>(`/music/manifest?${query.toString()}`);
        return response.data.data;
    },
    track: async (id: string): Promise<MusicTrack> => {
        const response = await api.get<{ data: { track: MusicTrack } }>(`/music/tracks/${encodeURIComponent(id)}`);
        return response.data.data.track;
    },
    upload: async (file: File, onProgress?: (fraction: number) => void): Promise<MusicTrack> => {
        const form = new FormData();
        form.append('file', file);
        const response = await api.post<{ data: { track: MusicTrack } }>('/music/tracks', form, {
            onUploadProgress: (event) => {
                if (onProgress && event.total) {
                    onProgress(event.loaded / event.total);
                }
            },
        });
        return response.data.data.track;
    },
    updateTrack: async (id: string, payload: MusicTrackUpdate) => {
        const response = await api.put<{ data: { track: MusicTrack } }>(`/music/tracks/${encodeURIComponent(id)}`, payload);
        return response.data.data.track;
    },
    deleteTrack: async (id: string): Promise<void> => {
        await api.delete(`/music/tracks/${encodeURIComponent(id)}`);
    },
    streamUrl: async (id: string): Promise<string> => {
        const response = await api.get<{ data: { url: string } }>(`/music/tracks/${encodeURIComponent(id)}/stream-url`);
        return response.data.data.url;
    },

    playlists: async (): Promise<MusicPlaylist[]> => {
        const response = await api.get<{ data: { playlists: MusicPlaylist[] } }>('/music/playlists');
        return response.data.data.playlists ?? [];
    },
    playlist: async (id: string): Promise<MusicPlaylist> => {
        const response = await api.get<{ data: { playlist: MusicPlaylist } }>(`/music/playlists/${encodeURIComponent(id)}`);
        return response.data.data.playlist;
    },
    createPlaylist: async (name: string, trackIds: string[] = []): Promise<MusicPlaylist> => {
        const response = await api.post<{ data: { playlist: MusicPlaylist } }>('/music/playlists', { name, track_ids: trackIds });
        return response.data.data.playlist;
    },
    updatePlaylist: async (id: string, payload: { name?: string; track_ids?: string[] }): Promise<MusicPlaylist> => {
        const response = await api.put<{ data: { playlist: MusicPlaylist } }>(`/music/playlists/${encodeURIComponent(id)}`, payload);
        return response.data.data.playlist;
    },
    deletePlaylist: async (id: string): Promise<void> => {
        await api.delete(`/music/playlists/${encodeURIComponent(id)}`);
    },

    player: async (player = WEB_PLAYER_NAME): Promise<MusicPlayerState> => {
        const response = await api.get<{ data: { player: MusicPlayerState } }>(`/music/player?player=${encodeURIComponent(player)}`);
        return response.data.data.player;
    },
    reportState: async (
        state: Partial<Pick<MusicPlayerState, 'track_id' | 'playlist_id' | 'queue' | 'position_seconds' | 'is_playing' | 'volume' | 'shuffle' | 'repeat'>>,
        player = WEB_PLAYER_NAME,
    ): Promise<MusicPlayerState> => {
        const response = await api.put<{ data: { player: MusicPlayerState } }>('/music/player', { ...state, player });
        return response.data.data.player;
    },
    command: async (action: MusicCommandAction, extra: Record<string, unknown> = {}, player = WEB_PLAYER_NAME) => {
        const response = await api.post<{ data: { player: MusicPlayerState } }>('/music/player/commands', { action, player, ...extra });
        return response.data.data.player;
    },
};

export const formatDuration = (seconds: number | null | undefined): string => {
    if (seconds == null || !Number.isFinite(seconds)) {
        return '—';
    }
    const total = Math.max(0, Math.round(seconds));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
};

export const formatBytes = (bytes: number | null | undefined): string => {
    if (bytes == null || !Number.isFinite(bytes)) {
        return '—';
    }
    if (bytes >= 1024 ** 3) {
        return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
    }
    return `${(bytes / 1024 ** 2).toFixed(bytes >= 10 * 1024 ** 2 ? 0 : 1)} MB`;
};

/** Triggers a browser download of a (pre-signed) URL without leaving the page. */
export const triggerDownload = (url: string, filename?: string): void => {
    const anchor = document.createElement('a');
    anchor.href = url;
    if (filename) {
        anchor.download = filename;
    }
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
};
